import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, Trash2, Check } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { AppShell } from "@/components/AppShell";
import { StartHereCard } from "@/components/onboarding/StartHereCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DAY_LABELS, currentWeekDates, toDateKey, weekStart } from "@/lib/hub";
import { useDomainPlan } from "@/lib/plan";
import { GoalPicker, PlanChip, type PlanLink } from "@/components/GoalPicker";

export const Route = createFileRoute("/_authenticated/habits")({
  head: () => ({
    meta: [
      { title: "Habits — MyPriorities" },
      {
        name: "description",
        content:
          "Track daily and weekly habits against your personal goals and pillars, and build a weekly wellbeing score.",
      },
      { property: "og:title", content: "Habits — MyPriorities" },
      {
        property: "og:description",
        content: "Check off habits tied to your personal goals and watch your weekly score build.",
      },
    ],
  }),
  component: Habits,
});

type Habit = {
  id: string;
  name: string;
  target_per_week: number;
  goal_id: string | null;
  pillar_id: string | null;
};
type Log = { id: string; habit_id: string; log_date: string };

function Habits() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("7");
  const [link, setLink] = useState<PlanLink>({ goal_id: null, pillar_id: null });
  const plan = useDomainPlan("personal");

  const week = currentWeekDates();
  const start = weekStart();
  const end = toDateKey(week[6]!);
  const today = toDateKey(new Date());

  const habitsQuery = useQuery({
    queryKey: ["habits"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("habits")
        .select("*")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as Habit[];
    },
  });

  const logsQuery = useQuery({
    queryKey: ["habit_logs", start],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("habit_logs")
        .select("*")
        .gte("log_date", start)
        .lte("log_date", end);
      if (error) throw error;
      return data as Log[];
    },
  });

  const habits = habitsQuery.data ?? [];
  const logs = logsQuery.data ?? [];

  const createHabit = useMutation({
    mutationFn: async () => {
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase.from("habits").insert({
        user_id: auth.user!.id,
        name: name.trim(),
        target_per_week: Number(target) || 1,
        goal_id: link.goal_id,
        pillar_id: link.pillar_id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setCreating(false);
      setName("");
      setTarget("7");
      setLink({ goal_id: null, pillar_id: null });
      queryClient.invalidateQueries({ queryKey: ["habits"] });
    },
  });

  const deleteHabit = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("habits").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["habits"] });
      queryClient.invalidateQueries({ queryKey: ["habit_logs", start] });
    },
  });

  const toggleLog = useMutation({
    mutationFn: async ({ habitId, date }: { habitId: string; date: string }) => {
      const existing = logs.find((l) => l.habit_id === habitId && l.log_date === date);
      if (existing) {
        const { error } = await supabase.from("habit_logs").delete().eq("id", existing.id);
        if (error) throw error;
        return;
      }
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("habit_logs")
        .insert({ user_id: auth.user!.id, habit_id: habitId, log_date: date });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["habit_logs", start] }),
  });

  const totalTarget = habits.reduce((s, h) => s + h.target_per_week, 0);
  const totalDone = habits.reduce(
    (s, h) => s + Math.min(h.target_per_week, logs.filter((l) => l.habit_id === h.id).length),
    0,
  );
  const score = totalTarget ? Math.round((totalDone / totalTarget) * 100) : 0;

  const pillarScores = plan.pillars
    .map((p) => {
      const owned = habits.filter((h) => h.pillar_id === p.id);
      const t = owned.reduce((s, h) => s + h.target_per_week, 0);
      const d = owned.reduce(
        (s, h) => s + Math.min(h.target_per_week, logs.filter((l) => l.habit_id === h.id).length),
        0,
      );
      return { id: p.id, name: p.name, target: t, done: d, pct: t ? (d / t) * 100 : 0 };
    })
    .filter((p) => p.target > 0);

  return (
    <AppShell
      title="Habits"
      subtitle="Check things off — your weekly wellbeing score builds as you go."
      action={
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" /> New habit
        </Button>
      }
    >
      <StartHereCard
        tipKey="habits"
        title="Keep the pace sustainable"
        body="Add a habit, set it to daily or a number of times a week, then tick it off as you go. Your weekly wellbeing score is simply check-offs against target — an honest read on whether the way you're working is holding up."
      >
        <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
          Add your first habit
        </Button>
      </StartHereCard>

      <section className="panel p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="label-caps">Weekly wellbeing score</h2>
            <p className="mt-2 font-display text-4xl font-bold text-primary">{score}%</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {totalDone} of {totalTarget} habit check-offs this week
            </p>
          </div>
          <div className="w-full max-w-sm">
            <Progress value={score} />
          </div>
        </div>

        {pillarScores.length ? (
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {pillarScores.map((p) => (
              <div key={p.id} className="rounded-md border border-border bg-surface p-4">
                <div className="flex items-baseline justify-between">
                  <p className="text-sm font-medium">{p.name}</p>
                  <span className="text-xs text-muted-foreground">
                    {p.done}/{p.target}
                  </span>
                </div>
                <Progress value={Math.min(100, p.pct)} className="mt-2" />
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="mt-8 space-y-3">
        {habits.length === 0 ? (
          <div className="panel p-10 text-center text-sm text-muted-foreground">
            No habits yet — add one like “Read 10 pages” (daily) or “Gym” (3x per week).
          </div>
        ) : (
          habits.map((h) => {
            const done = logs.filter((l) => l.habit_id === h.id).length;
            const pct = Math.min(100, (done / h.target_per_week) * 100);
            return (
              <div key={h.id} className="panel p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-semibold">{h.name}</h3>
                  <PlanChip
                    pillarName={plan.pillarName(h.pillar_id)}
                    goalName={plan.goalName(h.goal_id)}
                  />

                  <span className="text-xs text-muted-foreground">
                    {done}/{h.target_per_week} this week
                  </span>
                  {done >= h.target_per_week ? (
                    <span className="rounded-full bg-success/15 px-2 py-0.5 text-xs text-success">
                      Target hit
                    </span>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-auto"
                    onClick={() => deleteHabit.mutate(h.id)}
                    aria-label={`Delete ${h.name}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>

                <Progress value={pct} className="mt-3" />

                <div className="mt-4 flex gap-2">
                  {week.map((d, i) => {
                    const key = toDateKey(d);
                    const checked = logs.some((l) => l.habit_id === h.id && l.log_date === key);
                    const future = key > today;
                    return (
                      <button
                        key={key}
                        disabled={future}
                        onClick={() => toggleLog.mutate({ habitId: h.id, date: key })}
                        aria-label={`${h.name} on ${key}`}
                        className={`flex size-10 flex-col items-center justify-center rounded-md border text-xs transition-colors ${
                          checked
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-surface text-muted-foreground hover:bg-accent"
                        } ${future ? "opacity-40" : ""} ${key === today && !checked ? "border-primary/60" : ""}`}
                      >
                        {checked ? <Check className="size-4" /> : <span>{DAY_LABELS[i]}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </section>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New habit</DialogTitle>
            <DialogDescription>How often do you want to do it each week?</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="hname">Habit</Label>
              <Input
                id="hname"
                placeholder="e.g. Read 10 pages"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Times per week</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n === 7 ? "7 — daily" : `${n}x per week`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <GoalPicker
              idPrefix="habit"
              pillars={plan.pillars}
              goals={plan.goals}
              value={link}
              onChange={setLink}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createHabit.mutate()}
              disabled={!name.trim() || createHabit.isPending}
            >
              Add habit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
